import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { BellRing, Volume2, VolumeX } from "lucide-react";
import { api } from "@/lib/api";
import { usePrefs } from "@/contexts/PrefsContext";
import { useLive } from "@/lib/live";

interface Summary { count: number; latest_created_at: string | null; expiring: { booking_id: string; customer_name: string; confirm_deadline: string }[] }
const Ctx = createContext<Summary | null>(null);
export const usePendingSummary = () => useContext(Ctx);

let audio: AudioContext | null = null;
const ping = () => {
  try {
    audio = audio || new (window.AudioContext || (window as any).webkitAudioContext)();
    if (audio.state === "suspended") audio.resume();
    [0, 0.18].forEach((d, i) => {
      const o = audio!.createOscillator();
      const g = audio!.createGain();
      o.frequency.value = i ? 1175 : 880;
      g.gain.setValueAtTime(0.0001, audio!.currentTime + d);
      g.gain.exponentialRampToValueAtTime(0.25, audio!.currentTime + d + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, audio!.currentTime + d + 0.25);
      o.connect(g).connect(audio!.destination);
      o.start(audio!.currentTime + d);
      o.stop(audio!.currentTime + d + 0.3);
    });
  } catch {}
};

export const VenueAlertsProvider = ({ enabled, children }: { enabled: boolean; children: React.ReactNode }) => {
  const { t } = usePrefs();
  const [s, setS] = useState<Summary | null>(null);
  const seen = useRef<string | null | undefined>(undefined);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get<Summary>("/venue/bookings/pending-summary");
      if (seen.current !== undefined && data.latest_created_at && (!seen.current || data.latest_created_at > seen.current)) {
        if (localStorage.getItem("wn_alert_sound") !== "off") ping();
        window.dispatchEvent(new Event("wn:notify"));
      }
      seen.current = data.latest_created_at && (!seen.current || data.latest_created_at > seen.current) ? data.latest_created_at : seen.current ?? null;
      setS(data);
    } catch {}
  }, [t]);
  useLive(() => { if (enabled) load(); });
  useEffect(() => {
    if (!enabled) return;
    const unlock = () => { audio = audio || new (window.AudioContext || (window as any).webkitAudioContext)(); audio.resume(); };
    window.addEventListener("pointerdown", unlock, { once: true });
    load();
    const id = setInterval(load, 20000);
    window.addEventListener("focus", load);
    return () => { clearInterval(id); window.removeEventListener("focus", load); window.removeEventListener("pointerdown", unlock); };
  }, [enabled, load]);
  return <Ctx.Provider value={enabled ? s : null}>{children}</Ctx.Provider>;
};

const minsLeft = (iso: string) => Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 60000));

export const ExpiringBanner = () => {
  const { t } = usePrefs();
  const s = usePendingSummary();
  const [sound, setSound] = useState(localStorage.getItem("wn_alert_sound") !== "off");
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 30000);
    return () => clearInterval(id);
  }, []);
  const list = (s?.expiring || []).filter((e) => minsLeft(e.confirm_deadline) > 0);
  if (!list.length) return null;
  const toggle = () => {
    localStorage.setItem("wn_alert_sound", sound ? "off" : "on");
    setSound(!sound);
  };
  return (
    <div data-testid="expiring-banner" className="mb-5 rounded-3xl border-2 border-amber-500/50 bg-amber-500/10 p-4 flex flex-wrap items-center gap-3 animate-in fade-in slide-in-from-top-2">
      <BellRing className="h-5 w-5 text-amber-600 animate-pulse" />
      <div className="flex-1 min-w-0">
        <p className="font-bold text-sm">{t("b6.expiring").replace("{n}", String(list.length))}</p>
        <p className="text-xs text-muted-foreground truncate">
          {list.slice(0, 3).map((e) => t("b6.expiringOne").replace("{name}", e.customer_name).replace("{m}", String(minsLeft(e.confirm_deadline)))).join(" · ")}
        </p>
      </div>
      <button onClick={toggle} data-testid="alert-sound-toggle" aria-label={t(sound ? "b6.soundOn" : "b6.soundOff")} className="h-9 w-9 rounded-full bg-background/70 grid place-items-center">
        {sound ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
      </button>
      <Link to="/venue/bookings" data-testid="expiring-banner-link" className="h-9 px-4 rounded-full bg-amber-600 text-white text-xs font-bold inline-flex items-center">{t("b6.review")}</Link>
    </div>
  );
};
